import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Spacing, Text } from '@reservamos/elements';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import DiscountRowItem from './DiscountRowItem';

/**
 * Sort the availability categories by priority and availability.
 *
 * @param {object} a - The first availability category to compare.
 * @param {object} b - The second availability category to compare.
 * @returns {number} - The result of the comparison.
 */
const sortByAvailabilityAndPriority = (a, b) => {
  if (a.availability === 0 && b.availability !== 0) return 1;
  if (b.availability === 0 && a.availability !== 0) return -1;
  return a.priority - b.priority;
};

/**
 * AvailabilityDiscounts component
 *
 * Render a list of availability discounts.
 *
 * If the title is not in translations, it will show generic version.
 * Otherwise, it will show the title depending on the direction of the trip.
 *
 * If the prop related to the title visibility is not passed,
 * it will be shown by default.
 * Otherwise, it will be shown depending on the value of the prop.
 *
 * @param {object} props - availabilityCategories, titleContext, isTitleShown
 * @param {Array<object>} props.availabilityCategories - The availability categories to display.
 * @param {string} props.titleContext - The context of the title.
 * @param {boolean} props.isTitleShown - Whether the title should be shown.
 */
const AvailabilityDiscounts = ({ availabilityCategories, titleContext, isTitleShown }) => {
  const { t } = useTranslation();
  const { SHOW_CAPACITY_CATEGORIES } = useWhitelabelFeatures();
  const sortedAvailabilityCategories = availabilityCategories.sort(sortByAvailabilityAndPriority);

  /**
   * Build context for title translation based on ANTT mode and trip direction.
   *
   * Reads SHOW_CAPACITY_CATEGORIES and titleContext from closure scope to determine
   * the appropriate i18n context suffix for the discounts title.
   *
   * @returns {string} The context string for translation (e.g., 'antt', 'departs_antt').
   */
  const getTitleContext = () => {
    if (!SHOW_CAPACITY_CATEGORIES) return titleContext;

    if (titleContext) return `${titleContext}_antt`;

    return 'antt';
  };

  const titleContextValue = getTitleContext();
  const discountsTitle = t(
    'search:trip_discount_title',
    titleContextValue ? { context: titleContextValue } : {},
  );

  /**
   * Render column headers for ANTT mode.
   *
   * @returns {JSX.Element|null} The header row or null if not applicable.
   */
  const renderColumnHeaders = () =>
    SHOW_CAPACITY_CATEGORIES && (
      <div className="provider-info-discounts-header">
        <Text size="S" weight="semibold">
          {isTitleShown && discountsTitle}
        </Text>
        <Text size="S" weight="semibold">
          {t('search:discount_column_header_capacity')}
        </Text>
        <Text size="S" weight="semibold">
          {t('search:discount_column_header_availability')}
        </Text>
      </div>
    );

  return (
    <Spacing size="S" vertical>
      {isTitleShown && !SHOW_CAPACITY_CATEGORIES && <Text weight="bold">{discountsTitle}</Text>}
      <div className="provider-info-discounts-grid">
        {renderColumnHeaders()}
        {sortedAvailabilityCategories.map((category) => {
          const categoryType = t('category', { context: category?.type });
          // Extract capacity from category.capacity or category.meta.capacity (backend compatibility)
          const categoryCapacity = category?.capacity ?? category?.meta?.capacity;

          return (
            <DiscountRowItem
              key={categoryType}
              label={categoryType}
              availability={category?.availability}
              total={category?.total}
              capacity={categoryCapacity}
            />
          );
        })}
      </div>
    </Spacing>
  );
};

AvailabilityDiscounts.propTypes = {
  availabilityCategories: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      availability: PropTypes.number.isRequired,
      priority: PropTypes.number.isRequired,
      total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    }),
  ),
  titleContext: PropTypes.string,
  isTitleShown: PropTypes.bool,
};

AvailabilityDiscounts.defaultProps = {
  titleContext: '',
  isTitleShown: true,
};

export default AvailabilityDiscounts;
